import { useState, useEffect } from 'react';
import { Plus, X, ArrowDown, ChartBar } from '@phosphor-icons/react';
import { API } from './constants';

export const QueueSettings = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [preview, setPreview] = useState(null);

  const refreshPreview = () => {
    fetch(`${API}/email/queue/preview`, { credentials: 'include' })
      .then(r => r.json()).then(setPreview).catch(() => {});
  };

  useEffect(() => {
    fetch(`${API}/email/settings/queue`, { credentials: 'include' })
      .then(r => r.json()).then(setSettings).catch(() => {});
    refreshPreview();
    const t = setInterval(refreshPreview, 30000);
    return () => clearInterval(t);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const res = await fetch(`${API}/email/settings/queue`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    });
    const data = await res.json();
    setSettings(data);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
    refreshPreview();
  };

  const updateMin = (v) => setSettings(s => ({ ...s, min_interval_seconds: Math.max(5, parseInt(v) || 5) }));
  const updateMax = (v) => setSettings(s => ({ ...s, max_interval_seconds: Math.max(s.min_interval_seconds, parseInt(v) || s.min_interval_seconds) }));

  const updateTier = (idx, field, value) => {
    setSettings(s => {
      const tiers = [...(s.priority_tiers || [])];
      tiers[idx] = { ...tiers[idx], [field]: field === 'min_score' ? Math.max(0, Math.min(100, parseInt(value) || 0)) : value };
      return { ...s, priority_tiers: tiers };
    });
  };

  const addTier = () => {
    setSettings(s => ({
      ...s,
      priority_tiers: [...(s.priority_tiers || []), { name: `Tier ${(s.priority_tiers || []).length + 1}`, min_score: 0 }],
    }));
  };

  const removeTier = (idx) => {
    setSettings(s => {
      const tiers = [...(s.priority_tiers || [])];
      tiers.splice(idx, 1);
      return { ...s, priority_tiers: tiers };
    });
  };

  if (!settings) return null;

  // Sort for display: highest min_score first
  const sortedTiers = [...(settings.priority_tiers || [])].map((t, i) => ({ ...t, _origIdx: i })).sort((a, b) => b.min_score - a.min_score);

  const minMin = Math.floor(settings.min_interval_seconds / 60);
  const maxMin = Math.floor(settings.max_interval_seconds / 60);

  return (
    <div className="mt-8 space-y-4 border-t border-zinc-200 pt-8" data-testid="queue-settings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading text-base font-semibold">Wachtrij instellingen</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Random vertraging tussen verzendingen + lead-score prioritering</p>
        </div>
        <div className="flex items-center gap-2">
          {saved && <span className="text-xs text-emerald-600 font-medium">Opgeslagen!</span>}
          <button onClick={handleSave} disabled={saving} data-testid="save-queue-btn"
            className="px-4 py-2 text-sm font-medium bg-[#0055FF] text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {saving ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>

      <div className="border border-zinc-200 bg-white p-5 space-y-6">

        {/* Random Interval */}
        <div>
          <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-2 block">Random interval tussen verzendingen</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-xs text-zinc-500 mb-1 block">Minimum (seconden)</span>
              <div className="flex items-center gap-2">
                <input type="number" min="5" max="3600" value={settings.min_interval_seconds}
                  onChange={e => updateMin(e.target.value)}
                  className="border border-zinc-200 px-3 py-1.5 text-sm rounded-sm w-28" data-testid="queue-min-interval" />
                <span className="text-xs text-zinc-400">~ {minMin >= 1 ? `${minMin} min` : `${settings.min_interval_seconds}s`}</span>
              </div>
            </div>
            <div>
              <span className="text-xs text-zinc-500 mb-1 block">Maximum (seconden)</span>
              <div className="flex items-center gap-2">
                <input type="number" min={settings.min_interval_seconds} max="3600" value={settings.max_interval_seconds}
                  onChange={e => updateMax(e.target.value)}
                  className="border border-zinc-200 px-3 py-1.5 text-sm rounded-sm w-28" data-testid="queue-max-interval" />
                <span className="text-xs text-zinc-400">~ {maxMin >= 1 ? `${maxMin} min` : `${settings.max_interval_seconds}s`}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {[
              { lo: 30, hi: 90, label: '30s - 1.5min' },
              { lo: 60, hi: 300, label: '1 - 5 min' },
              { lo: 120, hi: 600, label: '2 - 10 min' },
              { lo: 300, hi: 900, label: '5 - 15 min' },
            ].map((p, i) => (
              <button key={i} type="button"
                onClick={() => setSettings(s => ({ ...s, min_interval_seconds: p.lo, max_interval_seconds: p.hi }))}
                data-testid={`interval-preset-${i}`}
                className={`px-2.5 py-1 text-xs border transition-colors ${
                  settings.min_interval_seconds === p.lo && settings.max_interval_seconds === p.hi
                    ? 'bg-[#0055FF] text-white border-[#0055FF]'
                    : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50'
                }`}>
                {p.label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-zinc-400 mt-2">Per email wordt een random vertraging gekozen tussen min en max — voorkomt herkenbaar patroon.</p>
        </div>

        {/* Priority Tiers */}
        <div className="border-t border-zinc-100 pt-5">
          <div className="flex items-center justify-between mb-2">
            <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Lead-score prioriteit</label>
            <button onClick={addTier} data-testid="add-tier-btn"
              className="flex items-center gap-1 px-2 py-1 text-xs text-[#0055FF] border border-blue-200 hover:bg-blue-50 transition-colors">
              <Plus size={12} /> Voeg tier toe
            </button>
          </div>
          <p className="text-[11px] text-zinc-500 mb-3">Hoogste tier (hoogste min_score) wordt eerst verzonden, dan tier 2, enz.</p>

          <div className="space-y-2">
            {sortedTiers.map((tier, displayIdx) => (
              <div key={tier._origIdx} data-testid={`priority-tier-${displayIdx}`}>
                <div className="flex items-center gap-2 p-3 border border-zinc-200 bg-zinc-50/50 rounded-sm">
                  <span className="w-7 h-7 flex items-center justify-center bg-[#0055FF] text-white text-xs font-bold shrink-0">P{displayIdx + 1}</span>
                  <input type="text" value={tier.name}
                    onChange={e => updateTier(tier._origIdx, 'name', e.target.value)}
                    placeholder="Tier naam"
                    className="flex-1 border border-zinc-200 px-2 py-1 text-sm rounded-sm min-w-0"
                    data-testid={`tier-${displayIdx}-name`} />
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-zinc-500 whitespace-nowrap">score ≥</span>
                    <input type="number" min="0" max="100" value={tier.min_score}
                      onChange={e => updateTier(tier._origIdx, 'min_score', e.target.value)}
                      className="border border-zinc-200 px-2 py-1 text-sm rounded-sm w-16 text-center"
                      data-testid={`tier-${displayIdx}-score`} />
                  </div>
                  <button onClick={() => removeTier(tier._origIdx)} data-testid={`tier-${displayIdx}-remove`}
                    disabled={(settings.priority_tiers || []).length <= 1}
                    className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <X size={14} />
                  </button>
                </div>
                {displayIdx < sortedTiers.length - 1 && (
                  <div className="flex justify-center my-1">
                    <ArrowDown size={14} className="text-zinc-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="text-[10px] text-zinc-400 mt-2">
            Tip: stel bijvoorbeeld P1 op score ≥ 80 (hete leads), P2 op ≥ 50, P3 op ≥ 0. Contacten zonder lead_score vallen automatisch in de laagste tier.
          </p>
        </div>

        {/* Live Preview */}
        {preview && (
          <div className="border-t border-zinc-100 pt-5" data-testid="queue-live-preview">
            <div className="flex items-center justify-between mb-3">
              <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider flex items-center gap-2">
                <ChartBar size={12} /> Live preview
              </label>
              <button onClick={refreshPreview} data-testid="refresh-preview-btn"
                className="text-[11px] text-[#0055FF] hover:underline">Vernieuwen</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Last intervals */}
              <div>
                <div className="text-xs text-zinc-500 mb-2">Laatste 5 intervallen tussen verzendingen</div>
                {preview.intervals.length === 0 ? (
                  <div className="text-xs text-zinc-400 italic px-3 py-2 border border-dashed border-zinc-200 rounded-sm">Nog geen recente verzendingen</div>
                ) : (
                  <div className="space-y-1">
                    {preview.intervals.map((g, i) => {
                      const inRange = g.seconds >= preview.settings.min_interval_seconds && g.seconds <= preview.settings.max_interval_seconds;
                      return (
                        <div key={i} className="flex items-center gap-2 text-xs px-2.5 py-1.5 bg-zinc-50 border border-zinc-200 rounded-sm" data-testid={`interval-row-${i}`}>
                          <span className={`font-data font-bold ${inRange ? 'text-emerald-600' : 'text-amber-600'}`}>{g.label}</span>
                          <span className="text-zinc-400 truncate flex-1">→ {g.to || 'unknown'}</span>
                          {g.step != null && <span className="text-[10px] text-zinc-400">stap {g.step}</span>}
                        </div>
                      );
                    })}
                    <div className="text-[10px] text-zinc-400 mt-1">
                      Groen = binnen ingesteld bereik ({preview.settings.min_interval_seconds}-{preview.settings.max_interval_seconds}s); oranje = buiten bereik
                    </div>
                  </div>
                )}
              </div>

              {/* Queue per tier */}
              <div>
                <div className="text-xs text-zinc-500 mb-2">Wachtrij per prioriteit-tier ({preview.total_queued} totaal)</div>
                {preview.total_queued === 0 ? (
                  <div className="text-xs text-zinc-400 italic px-3 py-2 border border-dashed border-zinc-200 rounded-sm">Wachtrij is leeg</div>
                ) : (
                  <div className="space-y-1">
                    {preview.tier_counts.map((t, i) => {
                      const pct = preview.total_queued > 0 ? Math.round((t.queued / preview.total_queued) * 100) : 0;
                      return (
                        <div key={i} className="px-2.5 py-1.5 bg-zinc-50 border border-zinc-200 rounded-sm" data-testid={`tier-queue-${i}`}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="font-medium">P{i + 1} {t.name} <span className="text-zinc-400 font-normal">(≥ {t.min_score})</span></span>
                            <span className="font-data font-bold text-[#0055FF]">{t.queued}</span>
                          </div>
                          <div className="w-full h-1 bg-zinc-200 rounded-sm overflow-hidden">
                            <div className="h-full bg-[#0055FF]" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                    {preview.fallback_queued > 0 && (
                      <div className="px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-sm text-xs flex items-center justify-between" data-testid="tier-fallback">
                        <span className="text-amber-700">Geen tier match (geen lead_score)</span>
                        <span className="font-data font-bold text-amber-700">{preview.fallback_queued}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
